import type { AnyAction } from 'redux';

import { WS_GRID_DEFAULT_PRESET_ID } from '@/domain/documents/constants';
import * as A from '@/domain/documents/gridPreset/gridPresetActions';
import { GridPresetStateFactory } from '@/domain/documents/gridPreset/factories';
import type { GridPresetStateRecord } from '@/domain/documents/gridPreset/factories';
import type { GridLayout } from '@/domain/documents/gridPreset/schemas';
import { layoutInGrid, normalizeLayout } from '@/domain/documents/gridPreset/convert';
import { isSameJson } from '@/lib/effectHelpers';

const bump = (state: GridPresetStateRecord) => state.update('revision', (revision) => revision + 1);

// The part of a layout this grid shows, as the working layout.
const inGrid = (state: GridPresetStateRecord, layout: GridLayout) => layoutInGrid(layout, state.defaultColumns);

const onDefaultColumnsChanged = (previous: GridPresetStateRecord, columns: GridLayout['columns']) => {
  const state = isSameJson(previous.defaultColumns, columns) ? previous : previous.set('defaultColumns', columns);
  const { saved, current } = state;
  if (state.presetId !== WS_GRID_DEFAULT_PRESET_ID || !saved || !current) return state;
  if (isSameJson(saved.columns, columns)) return state;
  // The user's own column changes stay; untouched columns follow the new definitions. Compared in the saved shape.
  const keepsColumns = !isSameJson(normalizeLayout(current).columns, normalizeLayout(saved).columns);
  const next = state.set('saved', { ...saved, columns });
  return keepsColumns ? next : bump(next.set('current', { ...current, columns }));
};

export const reducer = {
  documentsGridPreset(
    state: GridPresetStateRecord = GridPresetStateFactory(),
    action: AnyAction,
  ): GridPresetStateRecord {
    switch (action.type) {
      case A.gridPresetResolvedAction.type: {
        const {
          companyId,
          presetId,
          sharedPresetId,
          saved,
          current,
          defaultColumns,
          persist = true,
        } = action.payload as A.GridPresetResolvedPayload;
        return bump(
          state.merge({
            companyId,
            presetId,
            sharedPresetId,
            saved,
            current: layoutInGrid(current, defaultColumns),
            defaultColumns,
            persistable: persist,
          }),
        );
      }

      case A.gridPresetChosenAction.type: {
        const { presetId, saved } = action.payload as { presetId: string; saved: GridLayout };
        return bump(
          state.merge({
            presetId,
            saved,
            current: inGrid(state, saved),
            sharedPresetId: presetId === state.sharedPresetId ? state.sharedPresetId : null,
            persistable: true,
          }),
        );
      }

      case A.gridPresetResetAction.type:
        return state.saved ? bump(state.set('current', inGrid(state, state.saved))) : state;

      case A.gridPresetFiltersSetAction.type:
        return state.current
          ? bump(state.set('current', { ...state.current, filters: action.payload as Record<string, unknown> }))
          : state;

      case A.gridPresetGridStateReportedAction.type:
        return state.current ? state.set('current', action.payload as GridLayout) : state;

      case A.gridPresetSavedAction.type: {
        const { presetId, saved } = action.payload as { presetId: string; saved: GridLayout };
        return state.merge({
          presetId,
          saved,
          sharedPresetId: presetId === state.sharedPresetId ? state.sharedPresetId : null,
          persistable: true,
        });
      }

      case A.gridPresetDefaultColumnsChangedAction.type:
        return onDefaultColumnsChanged(state, action.payload as GridLayout['columns']);

      case A.gridPresetClearedAction.type:
        return GridPresetStateFactory().set('revision', state.revision);

      default:
        return state;
    }
  },
};
